<template>
  <ClientOnly>
    <div>
      <LoginFormComponent v-if="layout === layoutTypes.LOGIN"
                          data-test-id="login-form"
                          :layout-types="layoutTypes"
                          :error="loginError"
                          :email-not-verified="emailNotVerified"
                          @change-layout="changeLayout"
                          @login="login"
                          @resend="resend"
      />

      <RecoverFormComponent v-if="layout === layoutTypes.RECOVER"
                            data-test-id="recover-form"
                            :layout-types="layoutTypes"
                            :error="recoverError"
                            @change-layout="changeLayout"
                            @recover="recover"
      />

      <RecoveringFormComponent v-if="layout === layoutTypes.RECOVERING"
                               data-test-id="recovering-form"
                               :email="route.query.email"
                               :layout-types="layoutTypes"
                               :error="recoveringError"
                               @change-layout="changeLayout"
                               @recover="recoverPassword"
      />

      <RegisterFormComponent v-if="layout === layoutTypes.REGISTER"
                             data-test-id="register-form"
                             :layout-types="layoutTypes"
                             :error="registerError"
                             @change-layout="changeLayout"
                             @register="register"
      />

      <MessageEmailSent v-if="layout === layoutTypes.MESSAGE_EMAIL_SENT"
                        data-test-id="message-email-sent"
                        :layout-types="layoutTypes"
                        @change-layout="changeLayout"
      />

      <MessageAccountCreated v-if="layout === layoutTypes.MESSAGE_ACCOUNT_CREATED"
                             data-test-id="message-account-created"
                             :layout-types="layoutTypes"
                             :error="resendError"
                             @change-layout="changeLayout"
                             @resend="resend"
      />
    </div>
  </ClientOnly>
</template>

<script lang="ts">
export const layoutTypes = {
  LOGIN: 1,
  REGISTER: 2,
  RECOVER: 3,
  RECOVERING: 4,
  MESSAGE_EMAIL_SENT: 5,
  MESSAGE_ACCOUNT_CREATED: 6,
}
</script>
<script setup lang="ts">
import ClientOnly from '@/components/SSR/ClientOnly.vue'
import { useI18n } from 'vue-i18n'
import { useLocalizedRouter } from '@/composables/localizedRouter'
// import { useSentry } from '@/plugins/sentry'
import { TYPES as SNACK_TYPES } from '@/types/Snackbar'
import LoginFormComponent from './LoginFormComponent/LoginFormComponent.vue'
import RecoverFormComponent from './RecoverFormComponent/RecoverFormComponent.vue'
import RecoveringFormComponent from './RecoveringFormComponent/RecoveringFormComponent.vue'
import RegisterFormComponent from './RegisterFormComponent/RegisterFormComponent.vue'
import MessageEmailSent from './MessageEmailSent/MessageEmailSent.vue'
import MessageAccountCreated from './MessageAccountCreated/MessageAccountCreated.vue'
import { LOGIN_MODAL } from '@/types/Modals'
import { LoginError, useAuthHttp } from '@/http/authHttp'
import { useAuthStore } from '@/stores/useAuthStore'
import { computed, ref } from 'vue'
import { useModalsStore } from '@/stores/useModalsStore'
import { useSnackbarStore } from '@/stores/useSnackbarStore'
import { getCountry } from '@/router/utils/getCountry'
import { useModalStore } from '@/stores/useModalStore'
import { useUserStore } from '@/stores/useUserStore'
import { useRoute, useRouter } from 'vue-router'
// import { useGa } from '@/composables/useGa'

const route = useRoute()

const {
  register: registerHttp,
  resendEmail,
  recover: recoverHttp,
  recoverPassword: recoverPasswordHttp,
} = useAuthHttp()

const props = withDefaults(defineProps<{
  defaultLayout?: number
}>(), {
  defaultLayout: layoutTypes.LOGIN,
})

const emit = defineEmits([ 'login' ])

const { t } = useI18n()
const { localizedRoute } = useLocalizedRouter()
// const sentry = useSentry()
// const ga = useGa()

const navigatedLayout = ref(null)

// errors
const loginError = ref(null)
const recoverError = ref(null)
const recoveringError = ref(null)
const registerError = ref<string | null>(null)
const resendError = ref(null)
const emailNotVerified = ref<boolean>(false)

const layout = computed(() => navigatedLayout.value || props.defaultLayout)

const { login: loginHttp, profile, addresses } = useAuthHttp()
const { setToken } = useAuthStore()
const { toggle } = useModalsStore()
const { addSnack } = useSnackbarStore()
const { setUser, setAddresses } = useUserStore()
const router = useRouter()

function changeLayout(type) {
  navigatedLayout.value = type
}

async function login({ email, password }: { email: string, password: string }) {
  try {
    const res = await loginHttp(email, password)

    await setToken(res)

    const response = await profile()

    await setUser({ id: response.id, ...response.attributes })

    const data = await addresses()

    setAddresses(data.filter(address => address.attributes.country.code.toLowerCase() === getCountry().value).map(address => ({
          ...address,
          country: address.attributes.country.code.toLowerCase(),
        }),
    ))

    toggle(LOGIN_MODAL, false)

    // ga.trackLogin()

    emit('login')

  } catch (error) {
    if (error instanceof LoginError) {
      loginError.value = error.message || t('errors.login.incorrect')
      emailNotVerified.value = error.code === 'EMAIL_NOT_VERIFIED'

      return
    } else {
      // sentry.captureException(error)
    }
  }
}

async function recover({ email }) {
  try {
    await recoverHttp({ email })

    changeLayout(layoutTypes.MESSAGE_EMAIL_SENT)
    recoverError.value = null
  } catch (error) {
    recoverError.value = t('errors.recover.general')
  }
}

const { show } = useModalStore()

async function recoverPassword({ password, passwordConfirmation }) {
  try {
    await recoverPasswordHttp({
      token: route.query.token,
      email: route.query.email,
      password,
      passwordConfirmation,
    })

    addSnack({
      message: t('forms.messages.recovering-password-success'),
      type: SNACK_TYPES.SUCCESS,
    })

    router.push(localizedRoute({ name: 'login' }))
  } catch (e) {
    addSnack({
      message: t('forms.messages.recovering-password-error'),
      type: SNACK_TYPES.ERROR,
    })

    const serverMessage = e?.response?.data?.message

    if (serverMessage) {
      show({
        title: t('forms.messages.recovering-password-error'),
        message: serverMessage,
        callbacks: [
          {
            label: t('terms.ok'),
          },
        ],
      })
    }
  }
}

async function register({ name, email, password, passwordConfirmation }: {
  name: string,
  email: string,
  password: string,
  passwordConfirmation: string
}) {
  // Register post to server
  try {
    await registerHttp({
      name,
      email,
      password,
      passwordConfirmation,
    })

    // ga.trackSignUp()

    addSnack({
      message: t('forms.messages.creating-account-success'),
      type: SNACK_TYPES.SUCCESS,
    })

    registerError.value = null
    changeLayout(layoutTypes.MESSAGE_ACCOUNT_CREATED)

  } catch (e) {
    registerError.value = t('errors.register.general')

    addSnack({
      message: t('forms.messages.creating-account-error'),
      type: SNACK_TYPES.ERROR,
    })
  }
}

async function resend() {
  try {
    await resendEmail({ email: email.value })
    resendError.value = null
  } catch (e) {
    // sentry?.captureException(e)
    resendError.value = t('errors.resend.general')
  }
}
</script>
